import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { transportCardsApi } from 'services/apiDigitalTicket/transportCards';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import { ApplicationState } from 'store';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { listDevicesRequest } from '../devices/actions';

// Types

import * as TransportCardsActions from './actions';
import { TransportCardsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof TransportCardsActions.listTransportCardsRequest>): any {
  try {
    const isCsvFull = (payload as { isCsvFull?: boolean })?.isCsvFull;
    delete (payload as { isCsvFull?: boolean })?.isCsvFull;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsActions.listTransportCardsSuccess({
          transportCardsList: response.data,
          transportCardsListFull: response.data,
          isCsvFull,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (isCsvFull) {
        yield put(
          TransportCardsActions.listTransportCardsRequest({
            _includes: 'place',
            _limit: 10,
          }),
        );
      }
      // yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - listTransportCards - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.listTransportCardsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof TransportCardsActions.getTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsActions.getTransportCardSuccess({
          transportCardData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - getTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.getTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof TransportCardsActions.filterTransportCardsRequest>): any {
  try {
    const transportOperator = yield select(
      (state: ApplicationState) =>
        state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(transportCardsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsActions.filterTransportCardsSuccess({
          transportCardsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - filterTransportCards - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.filterTransportCardsFailure());
    meta.promise.reject(error);
  }
}

function* getFromNumber({
  payload,
}: ReturnType<
  typeof TransportCardsActions.getTransportCardFromNumberRequest
>): any {
  // Attention! This sagas should not have any feedback (Loading or Snackbar)
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200 && response.data.length > 0) {
      yield put(
        TransportCardsActions.getTransportCardFromNumberSuccess({
          transportCardData: response.data[0],
        }),
      );
    } else {
      yield put(TransportCardsActions.getTransportCardFromNumberFailure());
    }
  } catch (error) {
    console.error('transportCards - getTransportCardFromNumber - error', error);
    yield put(TransportCardsActions.getTransportCardFromNumberFailure());
  }
}

function* create({
  payload,
  meta,
}: ReturnType<typeof TransportCardsActions.createTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const { body, userId } = payload;

    const response = yield call(transportCardsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsActions.createTransportCardSuccess({
          transportCardData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão criado com sucesso',
        }),
      );
      yield meta.promise.resolve(response);
      if (userId) {
        yield put(
          listDevicesRequest({
            userId,
            _includes: 'profile',
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - createTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.createTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
}: ReturnType<typeof TransportCardsActions.createTransportCardsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando cartões' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.createList, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.createTransportCardsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartões criados com sucesso',
        }),
      );
      if (window.location.pathname === PATH.TRANSPORT_CARDS.BASE_URL) {
        yield put(TransportCardsActions.listTransportCardsRequest({}));
      } else {
        yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - createTransportCards - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.createTransportCardsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* activeDevice({
  payload,
}: ReturnType<typeof TransportCardsActions.activeDeviceRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando cartões' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.deviceActivate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.activeDeviceSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartões criados com sucesso',
        }),
      );
      if (window.location.pathname === PATH.TRANSPORT_CARDS.BASE_URL) {
        yield put(TransportCardsActions.listTransportCardsRequest({}));
      } else {
        yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - activeDevice - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.activeDeviceFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof TransportCardsActions.updateTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(TransportCardsActions.updateTransportCardSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão editado com sucesso',
        }),
      );
      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - updateTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.updateTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateStatus({
  payload,
}: ReturnType<
  typeof TransportCardsActions.updateTransportCardStatusRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando status' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(TransportCardsActions.updateTransportCardStatusSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão atualizado com sucesso',
        }),
      );
      yield put(
        TransportCardsActions.listTransportCardsRequest({
          _includes: 'place',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - updateTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.updateTransportCardStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof TransportCardsActions.deleteTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.deleteTransportCardSuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão excluído com sucesso',
        }),
      );

      if (window.location.pathname === PATH.TRANSPORT_CARDS.BASE_URL) {
        yield put(TransportCardsActions.listTransportCardsRequest({}));
        return;
      }

      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - deleteTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.deleteTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* requestCard({
  payload,
}: ReturnType<typeof TransportCardsActions.requestTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Solicitando cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.requestCard, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.requestTransportCardSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão solicitado com sucesso',
        }),
      );
      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - requestTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.requestTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* activateCard({
  payload,
}: ReturnType<typeof TransportCardsActions.activateTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Ativando cartão' }));
    const response = yield call(transportCardsApi.activateCard, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.activateTransportCardSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão ativado com sucesso',
        }),
      );
      yield put(push(PATH.TRANSPORT_CARDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - activateTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.activateTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* assignCard({
  payload,
  meta,
}: ReturnType<typeof TransportCardsActions.assignTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Vinculando cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.assignTransportCardSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão vinculado com sucesso',
        }),
      );

      if (meta.dispatchOnSuccess) {
        const { action, payload } = meta.dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - assignTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.assignTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* unassignCard({
  payload,
  meta,
}: ReturnType<typeof TransportCardsActions.unassignTransportCardRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Desvinculando cartão' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TransportCardsActions.unassignTransportCardSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Cartão desvinculado com sucesso',
        }),
      );

      if (meta.dispatchOnSuccess) {
        const { action, payload } = meta.dispatchOnSuccess;
        yield put(action(payload));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - unassignTransportCard - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.unassignTransportCardFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getTransportCardSettings({
  payload,
}: ReturnType<
  typeof TransportCardsActions.getTransportCardSettingsRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.getTransportCardSettings, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TransportCardsActions.getTransportCardSettingsSuccess({
          transportCardSettings: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportCards - getTransportCardSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.getTransportCardSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateTransportCardSettings({
  payload,
}: ReturnType<
  typeof TransportCardsActions.updateTransportCardSettingsRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(transportCardsApi.updateTransportCardSettings, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });
    if (response.status === 200) {
      yield put(TransportCardsActions.updateTransportCardSettingsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Atualizado com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error(
      'transportCards - updateTransportCardSettings - error',
      error,
    );
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(TransportCardsActions.updateTransportCardSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const transportCardsSagas = [
  takeLatest(TransportCardsTypes.LIST_TRANSPORT_CARDS_REQUEST, list),
  takeLatest(TransportCardsTypes.GET_TRANSPORT_CARD_REQUEST, get),
  takeLatest(TransportCardsTypes.FILTER_TRANSPORT_CARD_REQUEST, filter),
  takeLatest(
    TransportCardsTypes.GET_TRANSPORT_CARD_FROM_NUMBER_REQUEST,
    getFromNumber,
  ),
  takeLatest(TransportCardsTypes.CREATE_TRANSPORT_CARD_REQUEST, create),
  takeLatest(TransportCardsTypes.CREATE_TRANSPORT_CARDS_REQUEST, createList),
  takeLatest(TransportCardsTypes.UPDATE_TRANSPORT_CARD_REQUEST, update),
  takeLatest(
    TransportCardsTypes.UPDATE_TRANSPORT_CARD_STATUS_REQUEST,
    updateStatus,
  ),
  takeLatest(TransportCardsTypes.DELETE_TRANSPORT_CARD_REQUEST, remove),
  takeLatest(TransportCardsTypes.REQUEST_TRANSPORT_CARD_REQUEST, requestCard),
  takeLatest(TransportCardsTypes.ACTIVATE_TRANSPORT_CARD_REQUEST, activateCard),
  takeLatest(TransportCardsTypes.ASSIGN_TRANSPORT_CARD_REQUEST, assignCard),
  takeLatest(TransportCardsTypes.UNASSIGN_TRANSPORT_CARD_REQUEST, unassignCard),
  takeLatest(TransportCardsTypes.ACTIVE_DEVICE_REQUEST, activeDevice),
  takeLatest(
    TransportCardsTypes.GET_TRANSPORT_CARD_SETTINGS_REQUEST,
    getTransportCardSettings,
  ),
  takeLatest(
    TransportCardsTypes.UPDATE_TRANSPORT_CARD_SETTINGS_REQUEST,
    updateTransportCardSettings,
  ),
];
